import { resolveRelayBase, savedSessionToken, setFallbackRelayBase, setSessionReader, } from "./relay-url.js";
// Field caps mirror v1's `/api/client-error` route: message <= 4000, stack <= 12000.
const MAX_MESSAGE = 4000;
const MAX_STACK = 12000;
// Relay ingest path for client logs. (The relay route itself is wired separately;
// this package only emits the beacon.)
const CLIENT_LOG_PATH = "/api/cyborg/client-log";
// Lives on `globalThis`, not in module state, so it survives `vi.resetModules()`
// in the suites that re-import this module between cases.
const DELIVERY_FLAG = "__cyborg7ClientErrorDelivery__";
/**
 * Test-only override of the NETWORK beacon. `undefined` restores the environment
 * default. Never affects payload shaping or the local `console.error`.
 */
export function setClientErrorDelivery(enabled) {
    const g = globalThis;
    // A test that turns delivery on or off starts with a fresh send budget.
    deliveryBudget = new ClientErrorBudget();
    if (enabled === undefined)
        delete g[DELIVERY_FLAG];
    else
        g[DELIVERY_FLAG] = enabled;
}
function environmentDeliveryDefault() {
    try {
        // Read `process.env` off globalThis and go through a LOCAL variable: Vite
        // statically replaces the literal text `process.env.NODE_ENV` in a client
        // build, which would bake in "production" here and defeat the whole check.
        const g = globalThis;
        // Vitest parks these on the global in every node, jsdom and browser worker, so
        // a test that rewrites or clears `process.env` still cannot reopen delivery.
        if (g.__vitest_worker__ !== undefined || g.__vitest_browser__ !== undefined)
            return false;
        const env = g.process?.env;
        if (env?.VITEST)
            return false;
        if (env?.NODE_ENV === "test")
            return false;
        // A Vite BROWSER bundle has no `process`, so the checks above are blind in
        // vitest.config.ts's second project ("browser" — CI's required
        // "test:browser" chromium step), which mounts real components that call
        // reportClientError. Vitest sets MODE to "test" in its node AND browser
        // projects; a real build is "production"/"development", so prod delivers.
        // The cast is erased, so Vite still sees the literal `import.meta.env`.
        const mode = import.meta.env?.MODE;
        return mode !== "test";
    }
    catch {
        // An observability helper must never break the app: default to delivering.
        return true;
    }
}
/** Whether `sendPayload` may touch the network right now. */
export function clientErrorDeliveryEnabled() {
    const override = globalThis[DELIVERY_FLAG];
    if (typeof override === "boolean")
        return override;
    return environmentDeliveryDefault();
}
// Which client shell this page runs in, so a report from the desktop or a phone is
// not filed as "web". Tauri exposes its internals global; Electron names itself in
// the user agent.
export function detectClientPlatform() {
    try {
        const g = globalThis;
        const ua = g.navigator?.userAgent ?? "";
        if (g.__TAURI_INTERNALS__ !== undefined) {
            if (/iPhone|iPad|iPod/.test(ua))
                return "tauri-ios";
            if (/Android/.test(ua))
                return "tauri-android";
            return "tauri";
        }
        if (/Electron/.test(ua))
            return "desktop";
        return "web";
    }
    catch {
        // intentional: detection is best effort; an unknown shell reports as web
        return "web";
    }
}
// cast: globalThis is where the boot version and the build plugin's chunk ids live.
const globals = globalThis;
const CLIENT_VERSION = "__cyborg7ClientVersion__";
const CLIENT_AMBIENT = "__cyborg7ClientAmbient__";
const DEFAULT_FAILURE_KIND = "fault";
export function configureClientTelemetry(options) {
    if (options.version)
        globals[CLIENT_VERSION] = options.version;
    if (options.relayBase !== undefined)
        setFallbackRelayBase(options.relayBase);
    if ("session" in options)
        setSessionReader(options.session);
    if (options.ambient)
        globals[CLIENT_AMBIENT] = options.ambient;
}
function ambientContext() {
    try {
        const getter = globals[CLIENT_AMBIENT];
        return typeof getter === "function" ? (getter() ?? {}) : {};
    }
    catch {
        // intentional: a broken ambient getter must never lose the report it decorates
        return {};
    }
}
function configuredVersion() {
    const value = globals[CLIENT_VERSION];
    return typeof value === "string" ? value : undefined;
}
// PostHog's build plugin registers each bundle file's source-map id, keyed by a stack
// captured inside the file; only the files in this error's stack are sent.
const BUNDLE_URL_RE = /((?:https?|cyborg|tauri|app):\/\/[^\s()]+?):\d+:\d+/;
export function chunkIdsFor(stack) {
    if (!stack)
        return undefined;
    const registered = globals._posthogChunkIds;
    if (!registered || typeof registered !== "object")
        return undefined;
    const out = {};
    for (const [registrationStack, chunkId] of Object.entries(registered)) {
        const file = BUNDLE_URL_RE.exec(registrationStack)?.[1];
        if (file && typeof chunkId === "string" && stack.includes(file))
            out[file] = chunkId;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
// Build + clamp the beacon payload. Pure + exported for unit testing the clamping
// and field shaping. A caller's hardcoded "web" is a guess: the detected shell wins.
export function buildClientErrorPayload(input) {
    const { source, message, stack, ...explicit } = input;
    // Ambient sits UNDER the explicit input; a nullish explicit value does not hide it.
    const rest = { ...ambientContext() };
    for (const [key, value] of Object.entries(explicit)) {
        if (value != null || !(key in rest))
            rest[key] = value;
    }
    const clamped = stack ? String(stack).slice(0, MAX_STACK) : null;
    const chunkIds = chunkIdsFor(clamped);
    return {
        ...rest,
        failure_kind: typeof rest.failure_kind === "string" ? rest.failure_kind : DEFAULT_FAILURE_KIND,
        platform: typeof rest.platform === "string" && rest.platform !== "web"
            ? rest.platform
            : detectClientPlatform(),
        ...(typeof rest.version === "string" || configuredVersion()
            ? { version: typeof rest.version === "string" ? rest.version : configuredVersion() }
            : {}),
        ...(chunkIds ? { chunk_ids: chunkIds } : {}),
        source: source || "client",
        message: String(message ?? "Unknown client error").slice(0, MAX_MESSAGE),
        stack: clamped,
    };
}
function sendPayload(body) {
    // A test that configures the cloud relay would otherwise POST real beacons into
    // production's `client_log_error` stream (Node supplies `fetch`). A real build has
    // no `process` and MODE "production", so nothing changes there.
    if (!clientErrorDeliveryEnabled())
        return;
    const base = resolveRelayBase();
    // No session and no configured relay: nowhere this build may send to.
    if (base === undefined)
        return;
    const url = `${base}${CLIENT_LOG_PATH}`;
    // sendBeacon's credentials mode is fixed to "include" by spec — callers can't
    // opt out, unlike fetch. base === "" means the relay is the SAME origin as the
    // page (see resolveRelayBase), so the request isn't subject to CORS at all and
    // sendBeacon is safe — and it's the best unload-survival guarantee, so prefer
    // it there. Cross-origin (the desktop app's cyborg://app origin, or a web
    // deployment on a different host than the relay) IS subject to CORS, and the
    // relay's client-log route serves a wildcard Access-Control-Allow-Origin
    // (never credentialed). Browsers reject a credentialed response against a
    // wildcard ACAO — sendBeacon still returns true (it has no error signal, it
    // only confirms the browser queued the send) but the request is silently
    // dropped, so we'd go blind to desktop/cross-origin client errors. Skip
    // sendBeacon there and go straight to the credential-less fetch below.
    // The login token, when there is one, rides as a Bearer header so the relay can
    // verify the claimed workspace (it logs workspaceId null otherwise). sendBeacon
    // cannot carry a header, so a report with a token always goes through fetch.
    // Absent pre-login: the route logs anonymous reports too. No fetch → the tokenless
    // beacon still beats nothing.
    const token = savedSessionToken();
    if (base === "" && (!token || typeof fetch !== "function")) {
        try {
            if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
                const blob = new Blob([body], { type: "application/json" });
                if (navigator.sendBeacon(url, blob))
                    return;
            }
        }
        catch {
            // fall through to fetch
        }
    }
    // Fallback: keepalive fetch. Survives unload like sendBeacon (modern browsers
    // + Electron), and works cross-origin against the route's wildcard ACAO
    // because it's explicitly never credentialed — the relay doesn't need cookies
    // here (auth, when present, travels as a header/payload token, not a cookie).
    try {
        if (typeof fetch === "function") {
            void fetch(url, {
                method: "POST",
                headers: token
                    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
                    : { "Content-Type": "application/json" },
                body,
                keepalive: true,
                mode: "cors",
                credentials: "omit",
                // intentional: beacon is best-effort; local console.error already fired
            }).catch(() => { });
        }
    }
    catch {
        // Nothing else we can do — the console.error in reportClientError still fired.
    }
}
// A frozen view re-threw the same uncaught error on every hover, one keepalive POST each, which
// spent the browser's keepalive quota and lost the first, real error. The same report is sent once
// per window, and at most `perMinute` leave the page in any minute. Console is unlimited.
const UNCAUGHT_SOURCES = new Set(["window.onerror", "unhandledrejection"]);
export class ClientErrorBudget {
    sameWindowMs;
    perMinute;
    lastSent = new Map();
    sentAt = [];
    constructor(sameWindowMs = 60_000, perMinute = 30) {
        this.sameWindowMs = sameWindowMs;
        this.perMinute = perMinute;
    }
    allows(key, now) {
        const last = this.lastSent.get(key);
        if (last !== undefined && now - last < this.sameWindowMs)
            return false;
        this.sentAt = this.sentAt.filter((t) => now - t < 60_000);
        if (this.sentAt.length >= this.perMinute)
            return false;
        this.sentAt.push(now);
        // ponytail: the dedupe map is cleared at 500 keys, not aged out entry by entry.
        if (this.lastSent.size >= 500)
            this.lastSent.clear();
        this.lastSent.set(key, now);
        return true;
    }
}
let deliveryBudget = new ClientErrorBudget();
// Electron's console-message keeps only text (an object is "[object Object]"): JSON it.
export function consoleContextOf(payload) {
    const { source: _source, message: _message, stack, ...context } = payload;
    const top = stack?.split("\n").slice(0, 4).join("\n");
    const seen = new WeakSet();
    try {
        return JSON.stringify(top ? { ...context, stack: top } : context, (_key, value) => {
            if (typeof value === "bigint")
                return String(value);
            if (value instanceof Error)
                return { name: value.name, message: value.message };
            if (typeof value === "object" && value !== null) {
                if (seen.has(value))
                    return "[circular]";
                seen.add(value);
            }
            return value;
        });
    }
    catch {
        // intentional: the console line must never throw; the beacon carries the real payload
        return "[unserializable context]";
    }
}
/**
 * Report a client-side error. Builds + clamps the payload (message <= 4000,
 * stack <= 12000) and ALWAYS logs locally via console.error.
 *
 * Beacons to the relay's /api/cyborg/client-log (sendBeacon or keepalive fetch)
 * from web, the desktop renderer and the mobile WebView. The target is the session
 * the app's reader accepts, else the build relay from configureClientTelemetry;
 * with neither, nothing leaves the page. Never throws.
 */
export function reportClientError(input) {
    const payload = buildClientErrorPayload(input);
    // Always surface locally so a dev/console sees it even if delivery is dropped. An expected
    // outcome (a KPI, an expected user state) is not an error, so it is no red console line.
    try {
        if (payload.failure_kind === "expected") {
            // eslint-disable-next-line no-console
            console.info("[ClientEvent]", payload.source, input);
        }
        else {
            // eslint-disable-next-line no-console
            console.error("[ClientError]", payload.source, payload.message, consoleContextOf(payload));
        }
    }
    catch {
        // ignore
    }
    // The global handlers only: every other source is a count or throttles itself (reportUiFailure).
    const uncaught = UNCAUGHT_SOURCES.has(payload.source);
    if (uncaught && !deliveryBudget.allows(`${payload.source}\u0000${payload.message}`, Date.now()))
        return;
    try {
        sendPayload(JSON.stringify(payload));
    }
    catch {
        // Never let error reporting throw into the caller.
    }
}
//# sourceMappingURL=client-error.js.map